// ============================================================================
// Authentication
// ----------------------------------------------------------------------------
// Authorization Code flow with PKCE, against whatever OIDC provider the
// deployment is configured with. That one flow covers every case the product
// needs, which is why there is no branching for it here:
//
//   our SaaS      a Cognito user pool with Google and Entra federated in,
//                 plus username/password and email one-time codes;
//   self-hosted   the customer's own pool, or their own IdP directly, with
//                 SSO sign-up so their users never need provisioning here.
//
// All of that is configuration on the provider side. The application sees an
// ID token either way. That token says who the person is; lib/session.tsx
// exchanges it for a session naming the org (ADR 0007).
//
// There is a second provider, for development only. `APP_DEV_SUBJECT` puts the
// *server* into a mode where it accepts a fixed identity without verifying a
// token; without a matching path here the browser would still redirect to an
// issuer that was never configured, and a laptop with no identity provider
// would have no way into the application. The server advertises the mode on
// /api/config, and config.Load refuses to enable it outside APP_ENV=local.
//
// The identity provider's tokens are kept in sessionStorage — not localStorage,
// and not memory. localStorage outlives the tab and is shared by every tab on
// the origin. Memory alone would need an iframe renew with prompt=none after
// every reload, which Cognito's hosted UI does not support, so each reload
// would become a full redirect. sessionStorage is scoped to the tab and
// cleared when it closes. Like memory, it is readable by any script that gets
// onto the page; nothing here defends against that except not having one.
// The session token the API accepts is held in memory only (lib/session.tsx).
// A tab the browser opens itself starts without them, so it borrows them from
// a tab already open (lib/session-handoff.ts).
// ============================================================================

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { AuthProvider, useAuth as useOidcAuth } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";
import type { RuntimeConfig } from "@/lib/config";
import {
  broadcastSignOut,
  oidcUserKey,
  onSignOutElsewhere,
  receiveSession,
  serveSession,
} from "@/lib/session-handoff";
import { Splash } from "@/components/auth/Splash";

/**
 * What the application needs from authentication, independent of how it was
 * obtained. Both providers below supply exactly this, so no component has to
 * know which one is mounted.
 */
export interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  error?: string;
  /**
   * The ID token, not the access token: it is the one that carries email, and
   * provisioning a user without an address would leave them
   * unable to join an org at all. internal/auth checks `token_use` for exactly
   * this reason.
   */
  idToken: string | null;
  /**
   * Redirect to sign in. `provider` names a Cognito identity provider to go
   * straight to, for an org that requires its own.
   */
  signIn: (provider?: string) => void;
  signOut: () => void;
  /** True when running against the server's development identity. */
  isDev: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth was called outside AppAuthProvider");
  return ctx;
}

export function AppAuthProvider({
  config,
  children,
}: {
  config: RuntimeConfig;
  children: ReactNode;
}) {
  if (config.devAuth) {
    return <DevAuthProvider>{children}</DevAuthProvider>;
  }
  return (
    <SessionHandoff userKey={oidcUserKey(config.oidcIssuer, config.oidcClientId)}>
      <AuthProvider
        authority={config.oidcIssuer}
        client_id={config.oidcClientId}
        // The origin the browser is on. In a deployment that is the one hostname
        // registered with Cognito; locally it is whichever of :3000 or :8081 is
        // open, so neither needs its own configuration.
        redirect_uri={`${window.location.origin}/`}
        post_logout_redirect_uri={`${window.location.origin}/`}
        response_type="code"
        scope={config.oidcScopes || "openid profile email"}
        // The PKCE verifier and state must survive the redirect to the IdP and
        // back, so they go to sessionStorage — they are single-use and worthless
        // once the code has been exchanged. The tokens go there too; the header
        // of this file says why. Stated rather than inherited, although it is
        // the library's default: a default is what someone changes without
        // noticing what it meant.
        stateStore={new WebStorageStateStore({ store: window.sessionStorage })}
        userStore={new WebStorageStateStore({ store: window.sessionStorage })}
        automaticSilentRenew
        // Strip ?code= and ?state= after the exchange so a refresh does not
        // replay a consumed authorization code and land on an error page.
        onSigninCallback={() => {
          window.history.replaceState({}, document.title, window.location.pathname);
        }}
      >
        <OidcBridge config={config}>{children}</OidcBridge>
      </AuthProvider>
    </SessionHandoff>
  );
}

/**
 * Holds the auth library back until this tab has borrowed another tab's
 * tokens, if it had none and another tab had some. The library reads storage
 * once, when it mounts, so the borrowing has to finish first. A tab that
 * already has tokens does not wait at all; one that has none waits at most a
 * quarter of a second.
 */
function SessionHandoff({ userKey, children }: { userKey: string; children: ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let live = true;
    void receiveSession(userKey).then(() => {
      if (live) setReady(true);
    });
    return () => {
      live = false;
    };
  }, [userKey]);

  useEffect(() => serveSession(userKey), [userKey]);

  if (!ready) return <Splash message="Signing in…" />;
  return <>{children}</>;
}

const LINK_RETRY_KEY = "2dai.auth.link-retry";

function linkRetried(): boolean {
  try {
    return window.sessionStorage.getItem(LINK_RETRY_KEY) === "1";
  } catch {
    return true;
  }
}

/**
 * Cognito's answer to the first Google sign-in after the pre sign-up trigger
 * links it to an existing password account (ADR 0008). The link has been made;
 * the sign-in that made it fails anyway, and the next one succeeds.
 */
function isFirstLinkError(message: string | undefined): boolean {
  return !!message && message.includes("Already found an entry for username");
}

/** Maps react-oidc-context's shape onto AuthState. */
function OidcBridge({ config, children }: { config: RuntimeConfig; children: ReactNode }) {
  const oidc = useOidcAuth();

  // Retry the first-link failure once, silently: the person pressed "Continue
  // with Google" and should not be shown an error for Cognito's bookkeeping.
  // Once, because a second identical failure is a real problem to show.
  const linkError = isFirstLinkError(oidc.error?.message);
  useEffect(() => {
    let retried = false;
    try {
      retried = window.sessionStorage.getItem(LINK_RETRY_KEY) === "1";
      if (oidc.isAuthenticated) window.sessionStorage.removeItem(LINK_RETRY_KEY);
      else if (linkError && !retried) window.sessionStorage.setItem(LINK_RETRY_KEY, "1");
    } catch {
      /* no storage: at worst the error is shown instead of retried */
    }
    if (linkError && !retried) void oidc.signinRedirect();
  }, [linkError, oidc]);

  // Another tab signed out. Dropping the tokens is enough: this tab falls back
  // to the sign-in screen, and the provider's own session is already gone.
  const { removeUser } = oidc;
  useEffect(() => onSignOutElsewhere(() => void removeUser()), [removeUser]);

  const value = useMemo<AuthState>(
    () => ({
      isLoading: oidc.isLoading || (linkError && !linkRetried()),
      isAuthenticated: oidc.isAuthenticated,
      error: oidc.error?.message,
      idToken: oidc.user?.id_token ?? null,
      signIn: (provider?: string) =>
        void oidc.signinRedirect(
          provider ? { extraQueryParams: { identity_provider: provider } } : undefined,
        ),
      signOut: () => {
        void (async () => {
          broadcastSignOut();
          await oidc.removeUser();
          if (config.logoutEndpoint) {
            // Cognito's own sign-out: without it the Cognito session cookie
            // survives and the next "Sign in" signs straight back in.
            const q = new URLSearchParams({
              client_id: config.oidcClientId,
              logout_uri: `${window.location.origin}/`,
            });
            window.location.assign(`${config.logoutEndpoint}?${q}`);
          } else {
            await oidc.signoutRedirect();
          }
        })();
      },
      isDev: false,
    }),
    [oidc, config, linkError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Development only. The server is accepting a fixed identity, so the browser
 * sends a placeholder bearer token and skips the redirect entirely.
 *
 * The token's value is irrelevant — internal/auth's development verifier does
 * not read it — but something has to be sent, because the Authorization header
 * is what routes a request down the authenticated path at all.
 */
function DevAuthProvider({ children }: { children: ReactNode }) {
  const value = useMemo<AuthState>(
    () => ({
      isLoading: false,
      isAuthenticated: true,
      idToken: "dev",
      signIn: () => {
        // Already signed in as the development identity; there is nothing to
        // redirect to. Reaching this would mean the sign-in screen rendered,
        // which it cannot when isAuthenticated is true.
        return;
      },
      signOut: () => {
        window.alert(
          "This deployment is running with a development identity " +
            "(APP_DEV_SUBJECT), so there is no session to sign out of.",
        );
      },
      isDev: true,
    }),
    [],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
